import { useState } from 'react';
import { QAPair } from '../types';
import {
  NodeFilters,
  extractDepthLevels,
  extractRootNodes,
  getRootNodeLabel,
  FilterStats,
} from '../utils/nodeFilters';

interface FilterPanelProps {
  qaPairs: QAPair[];
  filters: NodeFilters;
  onFiltersChange: (filters: NodeFilters) => void;
  stats: FilterStats;
}

export default function FilterPanel({
  qaPairs,
  filters,
  onFiltersChange,
  stats,
}: FilterPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const depthLevels = extractDepthLevels(qaPairs);
  const rootNodes = extractRootNodes(qaPairs);

  // Depth level colors (matches node colors)
  const depthColors: Record<string, string> = {
    L1: '#3b82f6', // Blue
    L2: '#10b981', // Green
    L3: '#8b5cf6', // Purple
    L4: '#f59e0b', // Amber
    L5: '#ec4899', // Pink
  };

  const handleDepthToggle = (depth: string) => {
    const newDepths = new Set(filters.selectedDepths);
    if (newDepths.has(depth)) {
      newDepths.delete(depth);
    } else {
      newDepths.add(depth);
    }
    onFiltersChange({ ...filters, selectedDepths: newDepths });
  };

  const handleRootToggle = (rootId: string) => {
    const newRoots = new Set(filters.selectedRoots);
    if (newRoots.has(rootId)) {
      newRoots.delete(rootId);
    } else {
      newRoots.add(rootId);
    }
    onFiltersChange({ ...filters, selectedRoots: newRoots });
  };

  const handleDependencyToggle = () => {
    onFiltersChange({
      ...filters,
      hideNodesWithoutDependencies: !filters.hideNodesWithoutDependencies,
    });
  };

  const handleGhostNodesToggle = () => {
    onFiltersChange({
      ...filters,
      showGhostNodes: !filters.showGhostNodes,
    });
  };

  const handleSelectAllDepths = () => {
    onFiltersChange({
      ...filters,
      selectedDepths: new Set(depthLevels),
    });
  };

  const handleDeselectAllDepths = () => {
    onFiltersChange({
      ...filters,
      selectedDepths: new Set(),
    });
  };

  const handleSelectAllRoots = () => {
    onFiltersChange({
      ...filters,
      selectedRoots: new Set(rootNodes.map(r => r.question.id)),
    });
  };

  const handleDeselectAllRoots = () => {
    onFiltersChange({
      ...filters,
      selectedRoots: new Set(),
    });
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        background: '#ffffff',
        border: '2px solid #e2e8f0',
        borderRadius: '8px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        zIndex: 10,
        maxWidth: '350px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '18px' }}>🔍</span>
          <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b' }}>
            Filters
          </span>
        </div>
        <span style={{ fontSize: '12px', color: '#64748b' }}>
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>

      {isExpanded && (
        <div
          style={{
            padding: '16px',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {/* Stats */}
          <div
            style={{
              padding: '12px',
              background: '#f8fafc',
              borderRadius: '6px',
              marginBottom: '16px',
            }}
          >
            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
              Showing {stats.visibleNodes} of {stats.totalNodes} nodes
            </div>
            <div
              style={{
                height: '6px',
                background: '#e2e8f0',
                borderRadius: '3px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  background: '#10b981',
                  width: `${stats.percentageVisible}%`,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
              {stats.percentageVisible.toFixed(0)}% visible
            </div>
          </div>

          {/* Depth Filter */}
          <div style={{ marginBottom: '20px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <label
                style={{
                  fontSize: '13px',
                  fontWeight: 'bold',
                  color: '#475569',
                }}
              >
                Depth Levels
              </label>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  onClick={handleSelectAllDepths}
                  style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    background: 'none',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    color: '#64748b',
                  }}
                >
                  All
                </button>
                <button
                  onClick={handleDeselectAllDepths}
                  style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    background: 'none',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    color: '#64748b',
                  }}
                >
                  None
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {depthLevels.map(depth => (
                <label
                  key={depth}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '6px',
                    borderRadius: '4px',
                    background: filters.selectedDepths.has(depth)
                      ? '#f1f5f9'
                      : 'transparent',
                    transition: 'background 0.2s',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={filters.selectedDepths.has(depth)}
                    onChange={() => handleDepthToggle(depth)}
                    style={{ cursor: 'pointer' }}
                  />
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '2px',
                      background: depthColors[depth] || '#6b7280',
                    }}
                  />
                  <span style={{ fontSize: '13px', color: '#334155' }}>{depth}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Root Nodes Filter */}
          <div style={{ marginBottom: '20px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <label
                style={{
                  fontSize: '13px',
                  fontWeight: 'bold',
                  color: '#475569',
                }}
              >
                Root Branches
              </label>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  onClick={handleSelectAllRoots}
                  style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    background: 'none',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    color: '#64748b',
                  }}
                >
                  All
                </button>
                <button
                  onClick={handleDeselectAllRoots}
                  style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    background: 'none',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    color: '#64748b',
                  }}
                >
                  None
                </button>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                maxHeight: '200px',
                overflowY: 'auto',
              }}
            >
              {rootNodes.map(root => (
                <label
                  key={root.question.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '6px',
                    borderRadius: '4px',
                    background: filters.selectedRoots.has(root.question.id)
                      ? '#f1f5f9'
                      : 'transparent',
                    transition: 'background 0.2s',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={filters.selectedRoots.has(root.question.id)}
                    onChange={() => handleRootToggle(root.question.id)}
                    style={{ cursor: 'pointer', marginTop: '2px' }}
                  />
                  <span
                    style={{
                      fontSize: '12px',
                      color: '#334155',
                      lineHeight: '1.4',
                      flex: 1,
                    }}
                  >
                    {getRootNodeLabel(root)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Dependency Filter */}
          <div style={{ marginBottom: '8px' }}>
            <label
              style={{
                fontSize: '13px',
                fontWeight: 'bold',
                color: '#475569',
                display: 'block',
                marginBottom: '8px',
              }}
            >
              Dependencies
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '6px',
                  borderRadius: '4px',
                  background: filters.hideNodesWithoutDependencies
                    ? '#f1f5f9'
                    : 'transparent',
                  transition: 'background 0.2s',
                }}
              >
                <input
                  type="checkbox"
                  checked={filters.hideNodesWithoutDependencies}
                  onChange={handleDependencyToggle}
                  style={{ cursor: 'pointer', marginTop: '2px' }}
                />
                <span style={{ fontSize: '12px', color: '#334155', lineHeight: '1.4' }}>
                  Hide nodes without dependencies
                </span>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '6px',
                  borderRadius: '4px',
                  background: filters.showGhostNodes ? '#f1f5f9' : 'transparent',
                  transition: 'background 0.2s',
                }}
              >
                <input
                  type="checkbox"
                  checked={filters.showGhostNodes}
                  onChange={handleGhostNodesToggle}
                  style={{ cursor: 'pointer', marginTop: '2px' }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <span style={{ fontSize: '12px', color: '#334155', lineHeight: '1.4' }}>
                    Show ghost nodes
                  </span>
                  <span
                    style={{ fontSize: '10px', color: '#94a3b8', lineHeight: '1.3', marginTop: '2px' }}
                  >
                    Display filtered or missing dependency targets
                  </span>
                </div>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
